import i18n from '@/utils/i18n';
import { formatCurrency, MICRO_CONVERSION } from '@/utils/money';
import { getNumberFormatter } from '@/utils/number-format';

/**
 * Formats a currency amount from a string value.
 *
 * Converts a string representation of a currency amount (not in micros) to a
 * formatted currency string. The input string is converted to a number, multiplied
 * by MICRO_CONVERSION to convert to micros, then formatted using formatCurrency.
 *
 * If the input string cannot be converted to a valid number, the original string
 * is returned unchanged.
 *
 * @param amount - String representation of the currency amount (e.g., "15.50")
 * @param currencyCode - ISO currency code (e.g., "USD", "EUR") or null/undefined
 * @returns Formatted currency string (e.g., "$15.50") or the original string if invalid
 *
 * @example
 * formatCurrencyFromString("15.50", "USD") // Returns "$15.50"
 * formatCurrencyFromString("100", null) // Returns "$100.00" (defaults to USD)
 */
export const formatCurrencyFromString = (
  amount: string,
  currencyCode: string | null | undefined,
) => {
  const amountNumber = Number(amount);

  return isNaN(amountNumber)
    ? amount
    : formatCurrency(amountNumber * MICRO_CONVERSION, currencyCode);
};

/**
 * Formats a currency amount in micros using compact notation
 * (e.g., $13.6m, $48.9k).
 */
export const formatCompactCurrency = (
  amountInMicros: number,
  currencyCode: string,
): string =>
  formatCurrency(amountInMicros, currencyCode, {
    maximumFractionDigits: 1,
    notation: 'compact',
  }).toLowerCase();

/**
 * Formats a number using compact notation (e.g., 1.2k, 1m).
 */
export const formatCompactNumber = (value: number): string =>
  getNumberFormatter(i18n.locale, {
    maximumFractionDigits: 1,
    notation: 'compact',
  })
    .format(value)
    .toLowerCase();

/**
 * Formats a percentage value to a string.
 *
 * Converts a percentage value to a formatted string with exactly two decimal places,
 * unless it's a whole number. Truncates extra decimals beyond the second.
 * @param value - The percentage value to format
 *
 * @param maximumFractionDigits
 * @returns The formatted percentage string (e.g., "12.12%")
 *
 * @example
 * formatPercentage(12) // Returns "12%"
 * formatPercentage(12.1) // Returns "12.10%"
 * formatPercentage(12.12) // Returns "12.12%"
 * formatPercentage(12.123) // Returns "12.12%"
 */
export const formatPercentage = (
  value: number,
  maximumFractionDigits = 2,
  minimumFractionDigits?: number,
) => {
  const formatter = getNumberFormatter(i18n.locale, {
    maximumFractionDigits,
    minimumFractionDigits,
    style: 'percent',
  });
  return formatter.format(value / 100);
};
